import { Link } from "@remix-run/react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import isotipoImg from "~/assets/academia-isotipo-256.webp";
import { ThemeSwitch } from "~/components/ThemeToggle/ThemeSwitch";
import { trackingPublicConfigQueryOptions } from "~/lib/tracking/config";
import { openConsentPreferences } from "~/lib/tracking/consent";

import { SOCIAL_NETWORKS } from "../lib/socialLinks";

type FooterLink = { label: string; href: string };
type FooterColumn = { title: string; links: FooterLink[] };

/** Logo de una red en una sola forma: toma el color del contenedor. */
function SocialGlyph({ path, size = 18 }: { path: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d={path} />
    </svg>
  );
}

export function LandingNewFooter() {
  const { t } = useTranslation();
  // «Preferencias de cookies» sólo con el aviso prendido. En SSR y en el primer render del
  // cliente la config todavía no llegó: queda oculto en los dos (sin mismatch de hidratación).
  const { data: trackingConfig } = useQuery(trackingPublicConfigQueryOptions);
  const consentBanner = trackingConfig?.consentBanner === true;
  // 2026-07-31: una sola columna. Antes eran tres (Explorar / AcademIA / Legal) y las
  // dos primeras apuntaban a superficies que el lanzamiento ocultó (#rutas ya no existe
  // en la home, y Cursos y Quiénes somos salieron del nav).
  const columns = t("landing.footerNew.columns", {
    returnObjects: true,
  }) as Record<"useful", FooterColumn>;

  return (
    <footer className="relative z-10 bg-landing-ink-950 text-landing-on-dark">
      <div className="mx-auto max-w-[1280px] px-6 py-16 sm:px-8">
        {/* ═══ Top: brand + columns ═══ */}
        <div className="grid gap-12 md:grid-cols-[1fr_auto]">
          {/* Brand block */}
          <div>
            <Link
              to="/"
              data-cuelume-hover="tick"
              data-cuelume-press
              className="inline-flex items-center gap-2.5"
            >
              <img
                src={isotipoImg}
                alt=""
                width={44}
                height={44}
                className="h-10 w-10 object-contain"
              />
              <span className="font-display text-[26px] font-semibold leading-none tracking-tight">
                <span className="text-landing-on-dark">Academ</span>
                <span className="text-landing-red">IA</span>
              </span>
            </Link>
            <p className="mt-4 max-w-[280px] text-[14px] leading-relaxed text-[rgba(255,255,255,0.60)]">
              {t("landing.footerNew.tagline")}
            </p>
            {/* Redes: la lista y los handles viven en `lib/socialLinks.ts`, compartida con el
                `sameAs` del JSON-LD de la home. Acá sólo se dibuja.
                El `gap` es chico a propósito: cada link es una caja de 32px con el ícono de
                18px centrado, así que el aire entre glifos lo pone el padding, no el gap.
                Sumados dan los mismos 16px de separación visual que había cuando el link
                medía exactamente lo que el ícono. */}
            {/* `role="list"` explícito: el preflight de Tailwind pone `list-style: none` y con eso
                Safari/VoiceOver deja de anunciar la lista, nombre incluido. Para la regla es
                redundante; para el lector de pantalla, no. */}
            {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
            <ul
              role="list"
              aria-label="Redes de AcademIA"
              className="mt-5 flex flex-wrap items-center gap-x-0.5 gap-y-2"
            >
              {SOCIAL_NETWORKS.map((net) => (
                <li key={net.id} className="flex">
                  {net.href ? (
                    <a
                      href={net.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={net.ariaLabel}
                      data-cuelume-hover="tick"
                      data-cuelume-press
                      // El área de toque era de 18x18: el tamaño del ícono, sin padding. WCAG 2.2
                      // (2.5.8) pide 24x24 como mínimo y en mobile eso se falla tocando la red
                      // equivocada. La caja es de 32x32; el ícono sigue dibujándose a 18.
                      // El anillo de foco usa el mismo coral del hover: `landing-red` sobre el
                      // negro del footer no llega a 3:1.
                      className="inline-flex size-8 items-center justify-center rounded-md text-[rgba(255,255,255,0.50)] transition-colors hover:text-[#E2604F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E2604F] focus-visible:ring-offset-2 focus-visible:ring-offset-landing-ink-950"
                    >
                      <SocialGlyph path={net.path} />
                    </a>
                  ) : (
                    // Cuenta que todavía no existe: el logo se ve, pero no es un enlace (ni `#`,
                    // ni la home de la red). La etiqueta dice el estado a la vista; el nombre de
                    // la red lo completa para lectores de pantalla ("YouTube: Próximamente").
                    // El glifo va al 45% de blanco: sobre el ink-950 da ~4.4:1, arriba del 3:1
                    // que pide 1.4.11 para gráficos; al 35% quedaba justo en el límite.
                    <span
                      data-testid={`social-coming-soon-${net.id}`}
                      className="ml-1.5 inline-flex h-8 cursor-default select-none items-center gap-1.5 rounded-full border border-[rgba(255,255,255,0.14)] pl-2 pr-2.5 text-[12px] leading-none text-[rgba(255,255,255,0.55)]"
                    >
                      <span className="text-[rgba(255,255,255,0.45)]">
                        <SocialGlyph path={net.path} size={16} />
                      </span>
                      <span className="sr-only">{net.label}: </span>
                      Próximamente
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Columns */}
          {(["useful"] as const).map((key) => {
            const col = columns[key];
            return (
              <div key={key}>
                {/* #E2604F, no `landing-red`: el rojo de marca sobre el negro del footer
                    da 3.5:1 a 11px, abajo de AA. Es el mismo tono del hover de las redes. */}
                <p className="font-landing mb-4 text-[11px] font-semibold uppercase tracking-[0.12em] text-[#E2604F]">
                  {col.title}
                </p>
                <ul className="flex flex-col gap-2.5">
                  {col.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        to={link.href}
                        data-cuelume-hover="tick"
                        data-cuelume-press
                        className="text-[14px] text-[rgba(255,255,255,0.70)] transition-colors hover:text-landing-on-dark"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                  {consentBanner && (
                    <li>
                      {/* Reabre el aviso de cookies para cambiar la elección cuando quieras. */}
                      <button
                        type="button"
                        onClick={openConsentPreferences}
                        data-testid="footer-cookie-preferences"
                        className="text-left text-[14px] text-[rgba(255,255,255,0.70)] transition-colors hover:text-landing-on-dark"
                      >
                        Preferencias de cookies
                      </button>
                    </li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>

        {/* ═══ Bottom: divider + copyright ═══ */}
        <div className="mt-12 flex flex-col gap-4 border-t border-[rgba(255,255,255,0.10)] pt-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-[12px] text-[rgba(255,255,255,0.60)]">
            {t("landing.footerNew.copyright")}
          </p>
          <div className="flex flex-wrap items-center gap-3 sm:justify-end">
            <ThemeSwitch tone="on-dark" showStatus />
            <p className="font-landing text-[11px] tracking-[0.1em] text-[rgba(255,255,255,0.55)]">
              cursosacademia.ar
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
